/**
 * Collection AJAX filtering (Section Rendering API).
 *
 * Layers on top of the collection-with-filters section: intercepts filter
 * changes, sort/reset navigation, active-filter chips, and numbered
 * pagination, then re-renders the section in place via
 * `?section_id=...` and swaps the results/filters regions.
 *
 * Progressive enhancement contract:
 * - snippets/search-discovery-filters.liquid dispatches a cancelable
 *   `collection:filter-change`; uncanceled, the form GET-submits as before.
 * - The section's sort/reset handlers dispatch a cancelable
 *   `collection:navigate`; uncanceled, they assign window.location.
 * - Any fetch/parse failure falls back to a full page load.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ajax-filters]');
  if (!root || !window.fetch || !window.history || !window.URLSearchParams) return;

  var sectionId = root.getAttribute('data-section-id');
  var busy = false;

  function filterForm() {
    return document.querySelector('[data-sd-filters]');
  }

  function buildParams(overrides) {
    overrides = overrides || {};
    var params = new URLSearchParams();
    var form = filterForm();
    if (form) {
      new FormData(form).forEach(function (value, key) {
        if (value !== '') params.append(key, value);
      });
    }
    var current = new URLSearchParams(window.location.search);
    if (current.get('q')) params.set('q', current.get('q'));
    // Search page: keep how Shopify matches the terms and which result types it returns.
    ['options[prefix]', 'type'].forEach(function (key) {
      if (current.get(key) && !params.get(key)) params.set(key, current.get(key));
    });
    // `view` selects the per-page template; losing it would reset the page size.
    if (current.get('view')) params.set('view', current.get('view'));
    var sortSelect = document.querySelector('[data-sort-select]');
    var sortValue = overrides.sort_by || (sortSelect && sortSelect.value) || current.get('sort_by');
    if (sortValue && sortValue !== 'default') params.set('sort_by', sortValue);
    if (overrides.page && overrides.page !== '1') params.set('page', overrides.page);
    return params;
  }

  function openDetailsState() {
    var state = {};
    document.querySelectorAll('[data-sd-filters] details').forEach(function (details, index) {
      var summary = details.querySelector('summary');
      var key = summary ? summary.textContent.trim() : String(index);
      state[key] = details.open;
    });
    return state;
  }

  function restoreDetailsState(state) {
    document.querySelectorAll('[data-sd-filters] details').forEach(function (details, index) {
      var summary = details.querySelector('summary');
      var key = summary ? summary.textContent.trim() : String(index);
      if (key in state) details.open = state[key];
    });
  }

  function restoreViewMode() {
    var grid = document.querySelector('[data-product-grid]');
    if (!grid) return;
    var mode = 'grid';
    try { mode = window.localStorage.getItem('collection-view-mode') || 'grid'; } catch (error) {}
    grid.setAttribute('data-view', mode === 'list' ? 'list' : 'grid');
  }

  function swapRegion(doc, selector, mode) {
    var target = document.querySelector(selector);
    var incoming = doc.querySelector(selector);
    if (!target || !incoming) return;
    if (mode === 'text') target.textContent = incoming.textContent;
    else target.innerHTML = incoming.innerHTML;
  }

  function render(params, options) {
    options = options || {};
    if (busy) return;
    busy = true;

    var query = params.toString();
    var url = window.location.pathname + (query ? '?' + query : '');
    var fetchUrl = window.location.pathname + '?' + (query ? query + '&' : '') + 'section_id=' + encodeURIComponent(sectionId);

    var grid = document.querySelector('[data-product-grid]');
    if (grid) grid.setAttribute('aria-busy', 'true');
    root.classList.add('is-loading-results');

    fetch(fetchUrl, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Section render failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('[data-collection-results]')) throw new Error('Unexpected section markup');

        var detailsState = openDetailsState();
        swapRegion(doc, '[data-collection-results]', 'inner');
        swapRegion(doc, '[data-active-filters]', 'inner');
        swapRegion(doc, '[data-results-count]', 'text');
        swapRegion(doc, '[data-sd-filters]', 'inner');
        restoreDetailsState(detailsState);
        restoreViewMode();
        if (window.initSDFilters) window.initSDFilters();

        if (!options.skipHistory) window.history.pushState({ collectionAjax: true }, '', url);
        document.dispatchEvent(new CustomEvent('collection:results-updated'));

        if (options.scrollToResults) {
          var results = document.querySelector('[data-collection-results]');
          if (results) results.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      })
      .catch(function () {
        window.location.assign(url);
      })
      .finally(function () {
        busy = false;
        root.classList.remove('is-loading-results');
        var currentGrid = document.querySelector('[data-product-grid]');
        if (currentGrid) currentGrid.removeAttribute('aria-busy');
      });
  }

  // Filter inputs (debounced dispatch from search-discovery-filters).
  document.addEventListener('collection:filter-change', function (event) {
    event.preventDefault();
    render(buildParams());
  });

  // Sort select / reset button navigation from the section script.
  document.addEventListener('collection:navigate', function (event) {
    event.preventDefault();
    var target = new URL(event.detail.url, window.location.origin);
    render(new URLSearchParams(target.search));
  });

  // Active-filter chips, clear-all link, and numbered pagination links.
  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var inActiveFilters = link.closest('[data-active-filters]');
    var inPagination = link.closest('[data-collection-pagination]') && !link.hasAttribute('data-load-more');
    if (inPagination) {
      var grid = document.querySelector('[data-product-grid]');
      if (grid && grid.getAttribute('data-pagination-mode') !== 'numbered') return;
    }
    if (!inActiveFilters && !inPagination) return;

    var target = new URL(link.href, window.location.origin);
    if (target.pathname !== window.location.pathname) return;

    event.preventDefault();
    render(new URLSearchParams(target.search), { scrollToResults: inPagination });
  });

  // Back/forward restores the matching result set.
  window.addEventListener('popstate', function () {
    render(new URLSearchParams(window.location.search), { skipHistory: true });
  });
})();
